import React from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { inr, optimizeImage } from "@/lib/api";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";

export function FashionProductCard({ product }) {
  const { has, toggle } = useWishlist();
  const { user, setAuthOpen } = useAuth();
  if (!product) return null;

  const id = product._id || product.id;
  const slug = product.slug || id;
  const images = product.images || [];
  const mainImage = optimizeImage(images[0] || product.image, 500);
  const hoverImage = images.length > 1 ? optimizeImage(images[1], 500) : null;
  const wishlisted = has(id);

  const price = product.sale_price || product.price;
  const original = product.mrp || product.original_price;
  const hasDiscount = original && original > price;
  const discount = hasDiscount ? Math.round(((original - price) / original) * 100) : 0;

  return (
    <div className="fashion-card group relative">
      {/* Image */}
      <Link to={`/product/${slug}`} className="block fashion-card-image aspect-[3/4] bg-fashion-bg-warm">
        <img
          src={mainImage}
          alt={product.title || product.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {hoverImage && (
          <img
            src={hoverImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            loading="lazy"
          />
        )}

        {/* Badge */}
        {product.is_new && (
          <span className="absolute top-3 left-3 fashion-badge fashion-badge-new">New</span>
        )}
        {hasDiscount && discount >= 10 && (
          <span className="absolute top-3 left-3 fashion-badge fashion-badge-sale">-{discount}%</span>
        )}
      </Link>

      {/* Wishlist */}
      <button
        onClick={(e) => {
          e.preventDefault();
          if (!user) { setAuthOpen(true); return; }
          toggle(id);
        }}
        className="absolute top-3 right-3 p-2 bg-white/80 hover:bg-white rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100"
        style={{ opacity: wishlisted ? 1 : undefined }}
        aria-label="Wishlist"
      >
        <Heart
          className={`h-4 w-4 transition-colors ${wishlisted ? "fill-fashion-sale text-fashion-sale" : "text-fashion-text"}`}
          strokeWidth={1.5}
        />
      </button>

      {/* Info */}
      <div className="pt-3 pb-1 px-1">
        <Link to={`/product/${slug}`}>
          <p className="text-[13px] text-fashion-text-muted mb-0.5 truncate">
            {product.brand || "Farniymart"}
          </p>
          <h3 className="text-sm font-medium text-fashion-text truncate leading-snug">
            {product.title || product.name}
          </h3>
        </Link>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="fashion-price text-sm">{inr(price)}</span>
          {hasDiscount && (
            <>
              <span className="fashion-price-original text-xs">{inr(original)}</span>
              <span className="fashion-price-discount text-xs">({discount}% off)</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* Skeleton for loading state */
export function ProductCardSkeleton() {
  return (
    <div>
      <div className="aspect-[3/4] fashion-skeleton rounded-sm" />
      <div className="pt-3 space-y-2">
        <div className="h-3 w-16 fashion-skeleton rounded" />
        <div className="h-3.5 w-3/4 fashion-skeleton rounded" />
        <div className="h-3 w-20 fashion-skeleton rounded" />
      </div>
    </div>
  );
}
